import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MessageSquare, Mail, MailOpen, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useProfile } from "@/hooks/useProfile";
import {
  useGymMessages,
  useMarkMessageRead,
  useDeleteGymMessage,
} from "@/hooks/gym-messages/useGymMessages";

const MensajesSocios = () => {
  const { gymId } = useParams();
  const navigate = useNavigate();
  const { profile, isLoading: profileLoading } = useProfile();
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [authorized, setAuthorized] = useState<boolean | null>(null);

  // Authorization check
  useEffect(() => {
    const check = async () => {
      if (!gymId || !profile?.id) return;
      if (profile.role === "admin") {
        setAuthorized(true);
        return;
      }
      // recepcionista del gym (incluye dual-role: profes con rol extra de recepcionista)
      if (profile.gym_id === gymId) {
        const { data: hasRecepRole } = await supabase.rpc("has_role", {
          _user_id: profile.id,
          _role: "recepcionista",
        });
        if (hasRecepRole === true) {
          setAuthorized(true);
          return;
        }
      }
      // manager del gym (via gym_managers)
      const { data } = await supabase
        .from("gym_managers")
        .select("id")
        .eq("manager_id", profile.id)
        .eq("gym_id", gymId)
        .maybeSingle();
      setAuthorized(!!data);
    };
    if (!profileLoading) check();
  }, [gymId, profile?.id, profile?.role, profile?.gym_id, profileLoading]);

  const { data: gym } = useQuery({
    queryKey: ["gym", gymId],
    enabled: !!gymId && authorized === true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gyms")
        .select("id, name")
        .eq("id", gymId!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: messages, isLoading } = useGymMessages(
    authorized === true ? gymId : undefined
  );
  const markRead = useMarkMessageRead(gymId);
  const deleteMsg = useDeleteGymMessage(gymId);

  if (!gymId) {
    return (
      <div className="min-h-screen pt-16 bg-background flex items-center justify-center">
        <p className="text-destructive">ID de gimnasio no válido</p>
      </div>
    );
  }

  if (authorized === null || profileLoading) {
    return (
      <div className="min-h-screen pt-16 bg-background container py-6 space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (authorized === false) {
    return (
      <div className="min-h-screen pt-16 bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-destructive">No tienes permiso para ver esta sección</p>
        <Button onClick={() => navigate("/configuracion")}>Volver</Button>
      </div>
    );
  }

  const filtered = (messages ?? []).filter((m) =>
    filter === "unread" ? !m.is_read : true
  );
  const unreadCount = (messages ?? []).filter((m) => !m.is_read).length;

  return (
    <div className="min-h-screen pt-16 bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-16 z-10">
        <div className="container py-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/configuracion?tab=admin&subtab=gym")}
            className="mb-4 text-foreground hover:bg-muted"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <MessageSquare className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Mensajes de Socios
              </h1>
              <p className="text-muted-foreground">{gym?.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container py-6 space-y-4">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as "all" | "unread")}>
          <TabsList>
            <TabsTrigger value="all">Todos ({messages?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="unread">No leídos ({unreadCount})</TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-8 text-center">
            <MessageSquare className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-muted-foreground">
              {filter === "unread"
                ? "No hay mensajes sin leer"
                : "Aún no hay mensajes de socios"}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((msg) => {
              const fullName =
                [msg.sender?.first_name, msg.sender?.last_name]
                  .filter(Boolean)
                  .join(" ") || msg.sender?.username || "Socio";
              const initials =
                (msg.sender?.first_name?.[0] ?? "") +
                (msg.sender?.last_name?.[0] ?? "");
              return (
                <Card
                  key={msg.id}
                  className={`p-4 transition-all ${
                    !msg.is_read
                      ? "border-primary/40 bg-primary/[0.03]"
                      : "border-border/50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarImage src={msg.sender?.avatar_url ?? undefined} />
                      <AvatarFallback>
                        {initials.toUpperCase() || "S"}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="font-medium text-foreground">{fullName}</p>
                        {msg.sender?.dni && (
                          <span className="text-xs text-muted-foreground">
                            DNI: {msg.sender.dni}
                          </span>
                        )}
                        {!msg.is_read && (
                          <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4">
                            Nuevo
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {formatDistanceToNow(new Date(msg.created_at), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </p>
                      <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                        {msg.content}
                      </p>

                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            markRead.mutate({ id: msg.id, is_read: !msg.is_read })
                          }
                          disabled={markRead.isPending}
                        >
                          {msg.is_read ? (
                            <>
                              <Mail className="h-3.5 w-3.5 mr-1.5" />
                              Marcar no leído
                            </>
                          ) : (
                            <>
                              <MailOpen className="h-3.5 w-3.5 mr-1.5" />
                              Marcar leído
                            </>
                          )}
                        </Button>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                              Eliminar
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar mensaje?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción no se puede deshacer.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMsg.mutate(msg.id)}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default MensajesSocios;
